import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { FiscalData, TicketData } from "@/types/printer";
import type { PosVentaSinTicket } from "@/platform/types";
import type { SaleResult } from "@/context/caja/cola";
import { fiscalDataFromInvoice } from "@/utils/fiscalTicket";
import type { generateTicketHTML } from "@/pdfs/generateTicketHTML";
import { convertToNumberValue } from "@/utils/convertToNumber";
import { withVariant } from "@/utils/getCombinationString";
import { indexWarehouses, pickupPlaceFor } from "@/utils/ticketPickup";

/**
 * El ticket de una venta del mostrador, en los dos formatos que imprime la app:
 * el HTML (la impresión del navegador) y el de la térmica (ESC/POS o Epson).
 *
 * Es el que armaba a mano el botón «Imprimir ticket» después de la venta;
 * ahora lo usan ese botón, el ticket que sale solo después del commit de una
 * venta de la caja (trd-client#338 AC-A) y la pantalla de recuperación, que lo
 * guarda con la venta y lo reimprime.
 */
export type TicketHtml = Parameters<typeof generateTicketHTML>[0];

export interface TicketDeLaVenta {
  html: TicketHtml;
  termica: TicketData;
}

export interface DatosDeLaVenta {
  negocio: { name?: string; phone?: string; address?: string } | null;
  local: {
    name?: string;
    address?: { street?: string; streetNumber?: string; city?: string };
    /** Ya armada: la de la foto de la caja viene como texto (#346). */
    direccion?: string;
  } | null;
  vendedor: { name?: string; lastName?: string } | null;
  showSeller: boolean;
  orderItems: any[];
  looseOrderItems: any[];
  totalItemsAmount: unknown;
  total: unknown;
  appliedCoupons: any[];
  appliedCouponsAmount: unknown;
  appliedOffersAmount: unknown;
  paymentMethods: any[];
  warehousesById: ReturnType<typeof indexWarehouses>;
  /** El número de la caja (`C02-000123`), si la venta pasó por la cola. */
  numero?: string;
  fecha: Date;
}

/**
 * La dirección del local, como la imprime el ticket: la del servidor viene en
 * partes; la de la foto de la caja, ya armada (#346).
 */
export function direccionDelLocal(local: DatosDeLaVenta["local"]): string {
  if (local?.direccion !== undefined) return local.direccion;
  if (!local?.address) return "";
  return `${local.address.street || ""} ${local.address.streetNumber || ""}, ${
    local.address.city || ""
  }`.trim();
}

export function ticketDeLaVenta(d: DatosDeLaVenta): TicketDeLaVenta {
  const subtotalDeLasLineas = d.orderItems.reduce(
    (acc: number, item: any) => acc + item.basePrice * item.quantity,
    0
  );
  const totalDeLasLineas = d.orderItems.reduce(
    (acc: number, item: any) => acc + item.finalPrice,
    0
  );
  const descuentos = subtotalDeLasLineas - totalDeLasLineas;

  const direccion = direccionDelLocal(d.local);
  const vendedor = `${d.vendedor?.name || ""} ${
    d.vendedor?.lastName || ""
  }`.trim();
  const fecha = format(d.fecha, "dd/MM/yyyy HH:mm", { locale: es });

  const html: TicketHtml = {
    businessName: d.negocio?.name || "Negocio",
    businessPhone: d.negocio?.phone || "",
    businessAddress: d.negocio?.address || "",
    localName: d.local?.name || "Local",
    localAddress: direccion,
    sellerName: vendedor,
    showSeller: d.showSeller,
    productos: d.orderItems.map((item: any) => ({
      quantity: item.quantity,
      product: {
        title:
          item.product?.product?.title || item.product?.title || "Producto",
      },
      combinationString: item.combinationString,
      priceWithPlan: item.priceWithPlan,
      finalPrice: item.finalPrice,
      ...pickupPlaceFor(item, d.warehousesById),
    })),
    productosSueltos: d.looseOrderItems.map((item: any) => ({
      quantity: item.quantity,
      unitType: item.unitType,
      rel_product: {
        title: item.rel_product?.title || "Producto suelto",
      },
      combinationString: item.combinationString,
      finalPrice: item.finalPrice,
    })),
    subtotal: d.totalItemsAmount as number | string,
    discounts: descuentos > 0 ? descuentos : 0,
    appliedCoupons: d.appliedCoupons,
    appliedCouponsAmount: d.appliedCouponsAmount as number | string,
    appliedOffersAmount: d.appliedOffersAmount as number | string,
    total: d.total as number | string,
    paymentMethods: d.paymentMethods.map((pm: any) => ({
      checkoutFeeName: pm.checkoutFeeName,
      amount: pm.amount,
    })),
    fecha,
  };

  const termica: TicketData = {
    businessName: d.negocio?.name || "Negocio",
    localName: d.local?.name || undefined,
    localAddress: direccion || undefined,
    sellerName: vendedor,
    showSeller: d.showSeller,
    // La variante va PEGADA al nombre: la impresora termica solo recibe
    // `name`, y sin ella dos variantes del mismo producto salen con la misma
    // linea.
    products: d.orderItems.map((item: any) => ({
      name: withVariant(
        item.product?.product?.title || item.product?.title || "Producto",
        item.combinationString
      ),
      quantity: item.quantity,
      price: convertToNumberValue(item.finalPrice),
      ...pickupPlaceFor(item, d.warehousesById),
    })),
    looseProducts: d.looseOrderItems.map((item: any) => ({
      name: withVariant(
        item.rel_product?.title || "Producto suelto",
        item.combinationString
      ),
      quantity: item.quantity,
      unitType: item.unitType || "",
      price: convertToNumberValue(item.finalPrice),
    })),
    subtotal: convertToNumberValue(d.totalItemsAmount),
    discounts: descuentos > 0 ? descuentos : undefined,
    appliedCouponsAmount:
      convertToNumberValue(d.appliedCouponsAmount) || undefined,
    appliedOffersAmount:
      convertToNumberValue(d.appliedOffersAmount) || undefined,
    total: convertToNumberValue(d.total),
    paymentMethods: d.paymentMethods.map((pm: any) => ({
      name: pm.checkoutFeeName,
      amount: convertToNumberValue(pm.amount),
    })),
    date: fecha,
    // En la térmica sale como «Orden»: es lo que el cliente puede reclamar.
    ...(d.numero ? { orderId: d.numero } : {}),
  };

  return { html, termica };
}

/**
 * El mismo ticket con el comprobante y el código de retiro, que llegan después
 * (en la respuesta de la venta). Sin ellos sale «sin validez fiscal» y sin el
 * bloque de retiro.
 */
export function conElComprobante(
  ticket: TicketDeLaVenta,
  extra: { fiscalData?: FiscalData; pickupCode?: string | null }
): TicketDeLaVenta {
  return {
    html: {
      ...ticket.html,
      fiscalData: extra.fiscalData,
      pickupCode: extra.pickupCode ?? null,
    },
    termica: {
      ...ticket.termica,
      fiscalData: extra.fiscalData,
      pickupCode: extra.pickupCode ?? null,
    },
  };
}

/** El número de la caja en el ticket, que se sabe recién al registrar. */
export function conNumero(
  ticket: TicketDeLaVenta,
  numero: string
): TicketDeLaVenta {
  return { html: ticket.html, termica: { ...ticket.termica, orderId: numero } };
}

/**
 * El ticket de una venta que quedó sin imprimir, como hubiera salido: el que se
 * guardó con la venta, su número y, si ya llegó la respuesta, la factura y el
 * código de retiro (la pantalla de recuperación, #338 AC-C).
 */
export function ticketParaReimprimir(
  venta: PosVentaSinTicket
): TicketDeLaVenta {
  const respuesta = venta.respuesta as SaleResult | null;
  return conElComprobante(
    conNumero(venta.ticketData as unknown as TicketDeLaVenta, venta.ticket),
    {
      fiscalData: fiscalDataFromInvoice(respuesta?.invoice),
      pickupCode: respuesta?.pickupCode ?? null,
    }
  );
}
